import { FC, memo } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Flex, Link, Text, Wrap, WrapItem } from '@chakra-ui/react';
import { AccountContactMessagesGetConversationResponseMessage, ContactParticipant } from '@netiva/classifieds-api';

import { ApiBaseUrl } from '@/environment';

/**
 * Properties not worth repeating in a bubble: the advert title and contact name stand above the thread, the language
 * is plain to see from the message, and the client IP is for the backoffice.
 */
const HiddenPropertyKeys = new Set(['ClientIP', 'ContactName', 'DataObjectTitle', 'Language']);

export type MessageBubbleProps = {
  message: AccountContactMessagesGetConversationResponseMessage;
  /** Which side of the conversation the reader is on, which decides whose messages sit right. */
  participant: ContactParticipant;
};

export const MessageBubble: FC<MessageBubbleProps> = memo(function MessageBubble({ message, participant }) {
  const { t, i18n } = useTranslation();

  // An advertiser writes the outgoing messages, the person who enquired writes the incoming ones -
  // so the same message sits on opposite sides depending on who is reading the thread.
  const isOutgoing = participant === 'Advertiser' ? message.direction === 'Outgoing' : message.direction === 'Incoming';

  const properties = message.properties.filter((property) => !HiddenPropertyKeys.has(property.key));

  return (
    <Flex justify={isOutgoing ? 'flex-end' : 'flex-start'}>
      <Box
        maxW={{ base: '90%', md: '75%' }}
        bg={isOutgoing ? 'primary.50' : 'neutral.50'}
        borderWidth="1px"
        borderColor={isOutgoing ? 'primary.200' : 'neutral.200'}
        borderRadius="md"
        px={4}
        py={3}
      >
        <Text fontSize="xs" color="neutral.500" mb={1}>
          {isOutgoing
            ? t('messages.thread.you')
            : t(participant === 'Advertiser' ? 'messages.thread.contact' : 'messages.thread.advertiser')}
          {' · '}
          {new Date(message.dateCreated).toLocaleString(i18n.language)}
        </Text>

        <Text whiteSpace="pre-wrap">{message.content}</Text>

        {properties.length > 0 && (
          <Wrap mt={2} spacing={3}>
            {properties.map((property) => (
              <WrapItem key={property.key}>
                <Text fontSize="xs" color="neutral.600">
                  {t(`messages.properties.${property.key}`, { defaultValue: property.key })}: {property.value}
                </Text>
              </WrapItem>
            ))}
          </Wrap>
        )}

        {message.attachments.length > 0 && (
          <Wrap mt={2} spacing={3}>
            {message.attachments.map((attachment) => (
              <WrapItem key={attachment.id}>
                <Link
                  href={`${ApiBaseUrl}/v1/Account/ContactMessages/Attachments/${attachment.id}`}
                  target="_blank"
                  fontSize="sm"
                >
                  {attachment.name || t('messages.thread.attachment')}
                </Link>
              </WrapItem>
            ))}
          </Wrap>
        )}
      </Box>
    </Flex>
  );
});
